import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.http.*
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.*

@Serializable
data class TourSeasonDto(
    val id: String, val name: String,
    @SerialName("start_date") val startDate: String,
    @SerialName("end_date") val endDate: String? = null,
    val status: String = "draft", val description: String = "",
)

@Serializable
data class TourSeasonInput(
    val name: String,
    @SerialName("start_date") val startDate: String,
    @SerialName("end_date") val endDate: String?,
    val status: String, val description: String,
)

@Serializable
data class TourTournamentDto(
    val id: String,
    @SerialName("season_id") val seasonId: String,
    val title: String, val slug: String, val discipline: String,
    @SerialName("starts_on") val startsOn: String? = null,
    val status: String = "draft", val venue: String = "", val description: String = "",
    @SerialName("registration_revision") val registrationRevision: Long = 0,
    @SerialName("sequence_no") val sequenceNo: Int? = null,
    @SerialName("play_phase") val playPhase: String = "qualification",
    @SerialName("presentation_started") val presentationStarted: Boolean = false,
    @SerialName("play_revision") val playRevision: Long = 0,
    @SerialName("correction_mode") val correctionMode: Boolean = false,
)

@Serializable
data class TourScheduledTournament(
    val discipline: String,
    @SerialName("starts_on") val startsOn: String? = null,
)

@Serializable
data class TourPlayerDto(
    val id: String, val name: String,
    @SerialName("full_name") val fullName: String? = null,
    val gender: String? = null,
)

@Serializable
private data class TourPlayerPage(@SerialName("p_offset") val offset: Int)

@Serializable
private data class TourGuestRequest(
    @SerialName("p_id") val id: String,
    @SerialName("p_name") val name: String,
    @SerialName("p_full_name") val fullName: String,
    @SerialName("p_gender") val gender: String,
)

internal fun HttpRequestBuilder.tourAuth(token: String?) {
    header("apikey", ANON_KEY)
    header("Authorization", "Bearer ${token ?: ANON_KEY}")
    contentType(ContentType.Application.Json)
}

/** Tour only; never call league statistics, roster writes or the league player editor. */
object TourApi {
    suspend fun canManage(token: String): Boolean =
        httpClient.post("$SUPABASE_URL/rest/v1/rpc/tour_can_manage") {
            tourAuth(token); setBody(emptyMap<String, String>())
        }.body()

    internal suspend inline fun <reified T> rows(table: String, token: String?, order: String, filters: Map<String, String> = emptyMap(), columns: String = "*"): List<T> {
        val rows = mutableListOf<T>()
        while (true) {
            val page: List<T> = httpClient.get("$SUPABASE_URL/rest/v1/$table") {
                tourAuth(token)
                parameter("select", columns)
                parameter("order", order)
                parameter("limit", 200)
                parameter("offset", rows.size)
                filters.forEach { (key, value) -> parameter(key, value) }
            }.body()
            rows.addAll(page)
            if (page.size < 200) return rows
        }
    }

    suspend fun seasons(token: String?): List<TourSeasonDto> = rows("tour_season", token, "start_date.desc,id")
    suspend fun tournaments(token: String?, seasonId: String): List<TourTournamentDto> =
        rows("tour_tournament", token, "sequence_no.asc.nullslast,starts_on.asc.nullslast,id", mapOf("season_id" to "eq.$seasonId"))

    suspend fun createSeason(token: String, id: String, input: TourSeasonInput, schedule: List<TourScheduledTournament>) {
        httpClient.post("$SUPABASE_URL/rest/v1/rpc/tour_create_season") {
            tourAuth(token); setBody(buildJsonObject {
                put("p_id", id); put("p_name", input.name); put("p_start_date", input.startDate)
                put("p_end_date", input.endDate?.let(::JsonPrimitive) ?: JsonNull)
                put("p_status", input.status); put("p_description", input.description)
                put("p_tournaments", JsonArray(schedule.map { row -> buildJsonObject {
                    put("discipline", row.discipline); put("starts_on", row.startsOn?.let(::JsonPrimitive) ?: JsonNull)
                } }))
            })
        }
    }

    suspend fun saveSeason(token: String, id: String, input: TourSeasonInput) {
        val saved: List<TourSeasonDto> = httpClient.patch("$SUPABASE_URL/rest/v1/tour_season") {
            tourAuth(token); header("Prefer", "return=representation"); parameter("id", "eq.$id"); setBody(input)
        }.body()
        check(saved.size == 1) { "Ročník nebyl uložen. Ověřte oprávnění a načtěte data znovu." }
    }

    suspend fun saveTournament(token: String, tournament: TourTournamentDto, date: String?, status: String) {
        httpClient.post("$SUPABASE_URL/rest/v1/rpc/tour_update_tournament") {
            tourAuth(token); setBody(buildJsonObject {
                put("p_id", tournament.id); put("p_starts_on", date?.let(::JsonPrimitive) ?: JsonNull)
                put("p_status", status); put("p_expected_revision", tournament.registrationRevision)
            })
        }
    }

    suspend fun players(token: String): List<TourPlayerDto> {
        val players = mutableListOf<TourPlayerDto>()
        while (true) {
            val page: List<TourPlayerDto> = httpClient.post("$SUPABASE_URL/rest/v1/rpc/tour_list_players") {
                tourAuth(token); setBody(TourPlayerPage(players.size))
            }.body()
            players.addAll(page)
            if (page.size < 200) return players.sortedWith(compareBy(tourNameComparator) { it.name })
        }
    }

    suspend fun createGuest(token: String, id: String, name: String, fullName: String, gender: String): String =
        httpClient.post("$SUPABASE_URL/rest/v1/rpc/tour_create_guest") {
            tourAuth(token); setBody(TourGuestRequest(id, name, fullName, gender))
        }.body()

}

// Work in the active Tour season. An unpublished draft is usable for initial admin setup only.
internal fun currentTourSeason(seasons: List<TourSeasonDto>, admin: Boolean = false): TourSeasonDto? =
    seasons.firstOrNull { it.status == "active" } ?: if (admin) seasons.firstOrNull { it.status == "draft" } else null
